import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { invalidateLeaveRequestQueries } from '../../leave-request-queries';
import { getSickLeaveRequests, requestSickLeave } from '../services/sick-leave.service';
import {
  GetSickLeaveRequestsParams,
  SickLeaveRequestWorkflowInput,
  SickLeaveWorkflowResult,
} from '../types/sick-leave.types';

export const useGetSickLeaveRequests = (params: GetSickLeaveRequestsParams) => {
  return useGlobalQuery({
    queryKey: ['hr-sick-leave-requests', params],
    queryFn: () => getSickLeaveRequests(params),
    staleTime: 60 * 1000,
  });
};

/** Toasts the workflow's own answer: its refusal reason as a destructive toast, or `successKey` when
 * it went through. */
const useSickLeaveWorkflowToast = () => {
  const { toast } = useToast();
  const { t } = useTranslation();

  return (result: SickLeaveWorkflowResult, successKey: string) => {
    if (result.IsSuccessful) {
      toast({ title: result.Message || t(successKey) });
    } else {
      toast({ title: result.Message || t('SOMETHING_WENT_WRONG'), variant: 'destructive' });
    }
  };
};

export const useRequestSickLeave = () => {
  const queryClient = useQueryClient();
  const toastResult = useSickLeaveWorkflowToast();

  return useGlobalMutation({
    mutationFn: (input: SickLeaveRequestWorkflowInput) => requestSickLeave(input),
    onSuccess: (result) => {
      invalidateLeaveRequestQueries(queryClient);
      toastResult(result, 'SICK_LEAVE_REQUEST_SUBMITTED');
    },
  });
};
